import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Button,
  DataTable,
  Icon,
  InputText,
  NumberInput,
  Select,
  type ColumnDef,
  type SelectOption,
  SectionCard,
  PageWrapper,
} from "@/deps/ui-library";
import { useBankAccounts, useCreateFundTransfer } from "@/api/queries";
import { PageHeader } from "@/components/PageHeader";
import { today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { DateField } from "@/components/DateField";

/** The value an RDS single `Select` hands back, as the plain string this screen holds. */
function chosen(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

/** One line of the journal the transfer will post. */
interface JournalLine {
  /** Which side of the entry — the key, since both lines name the same account in a mistake. */
  side: "debit" | "credit";
  account: string;
  debit: string | null;
  credit: string | null;
}

const JOURNAL_COLUMNS: Array<ColumnDef<JournalLine>> = [
  {
    id: "account",
    header: "Account",
    cell: ({ row: { original: line } }) => line.account,
  },
  {
    id: "debit",
    header: "Debit",
    meta: { textAlign: "right" },
    cell: ({ row: { original: line } }) => <Amount value={line.debit} />,
  },
  {
    id: "credit",
    header: "Credit",
    meta: { textAlign: "right" },
    cell: ({ row: { original: line } }) => <Amount value={line.credit} />,
  },
];

/**
 * An amount, or an em dash rather than an empty cell: a screen reader reads silence the same as a
 * missing column, and "no credit" is the useful answer.
 */
function Amount({ value }: { value: string | null }) {
  return value === null ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="num">{formatAmount(value)}</span>
  );
}

/**
 * Move money between the company's own accounts.
 *
 * The screen shows the journal it will produce before it is recorded. A fund transfer is two lines
 * and no revenue — it changes where the cash is, never how much there is — and showing that is the
 * quickest way for somebody to notice they have picked the same account twice.
 */
export function NewFundTransferPage() {
  const navigate = useNavigate();
  const { data: accounts } = useBankAccounts();
  const [date, setDate] = useState(today());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const create = useCreateFundTransfer();

  const sameAccount = from !== "" && from === to;
  const value = Number(amount || "0");
  const fromAccount = accounts?.find((a) => a.id === from);
  const toAccount = accounts?.find((a) => a.id === to);

  const insufficient = fromAccount !== undefined && value > Number(fromAccount.balance);

  const canSave = from !== "" && to !== "" && !sameAccount && value > 0 && !create.isPending;

  const accountOptions: SelectOption[] = (accounts ?? []).map((account) => ({
    value: account.id,
    label: `${account.name} · ${formatAmount(account.balance)}`,
  }));
  const journal: JournalLine[] = [
    { side: "debit", account: toAccount?.name ?? "", debit: amount, credit: null },
    { side: "credit", account: fromAccount?.name ?? "", debit: null, credit: amount },
  ];

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title="New fund transfer"
        actions={
          <span className="flex gap-2">
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => void navigate({ to: "/banking/transfers" })}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={!canSave}
              onClick={() => {
                // The rejection used to be neither caught nor shown: a refused transfer left the
                // person on the screen with no message and the button live again.
                create.mutate(
                  { date, fromBankAccountId: from, toBankAccountId: to, amount, reference },
                  {
                    onSuccess: (transfer) =>
                      void navigate({ to: "/banking/transfers/$id", params: { id: transfer.id } }),
                  },
                );
              }}
            >
              {create.isPending ? "Recording" : "Record transfer"}
            </Button>
          </span>
        }
      />

      {create.isError ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={
            create.error instanceof Error
              ? create.error.message
              : "That transfer could not be recorded."
          }
        />
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Transfer">
          <div className="flex flex-col gap-3">
            <DateField label="Date" required value={date} onChange={setDate} />

            <Select
              label="From"
              required
              placeholder="Choose an account…"
              options={accountOptions}
              value={from}
              onChange={(details) => setFrom(chosen(details))}
            />

            <Select
              label="To"
              required
              placeholder="Choose an account…"
              options={accountOptions}
              value={to}
              onChange={(details) => setTo(chosen(details))}
              errorText={
                sameAccount
                  ? "Choose a different account. A transfer to itself moves nothing."
                  : undefined
              }
            />

            <NumberInput
              label="Amount"
              required
              value={amount}
              onValueChange={(details) => setAmount(details.value)}
              // The amount is sent as it stands, and RDS formats on blur: grouping off, because
              // "2,500" is not a decimal string; two places, because an amount on the wire has two.
              formatOptions={{
                useGrouping: false,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }}
              config={{ hideTriggers: true }}
              // A warning, not a block: an account can legitimately be overdrawn, and the software
              // does not know the arrangement with the bank.
              helperText={
                insufficient
                  ? `That is more than the ${formatAmount(fromAccount!.balance)} showing in ${fromAccount!.name}.`
                  : undefined
              }
            />

            <InputText
              label="Reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
        </SectionCard>

        <SectionCard title="The journal this will post">
          {from === "" || to === "" || value === 0 ? (
            <p className="text-fg-muted text-sm">
              Choose both accounts and an amount to see the entry.
            </p>
          ) : (
            <>
              <DataTable
                aria-label="Journal preview"
                size="sm"
                enableSorting={false}
                columns={JOURNAL_COLUMNS}
                data={journal}
                getRowId={(line) => line.side}
                pagination={{ mode: "manual", show: false }}
              />

              <p className="text-muted-foreground mt-3 flex items-start gap-1.5 text-xs">
                <Icon name="Info" size="sm" />
                <span>
                  Two lines, balanced, no revenue and no expense. Your total cash is unchanged —
                  only where it sits.
                </span>
              </p>
            </>
          )}
        </SectionCard>
      </div>
    </PageWrapper>
  );
}
